import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { format, subDays, addDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { apiClient } from '@/lib/api/client'
import type { OpsCurrencyTotals, OpsEventTotalsRow } from '@/lib/api/types'
import { useAuth } from '@/lib/auth'
import { DateFilter } from '@/components/ui/date-filter'

export const Route = createFileRoute('/_authenticated/ops/revenue')({
  // Superuser gating lives on the /ops layout route (ops.tsx).
  component: RevenuePage,
})

// Mirrors the backend default: 90 days back, 30 forward — upcoming events
// sell tokens before their start date and belong in "revenue right now".
const DEFAULT_START = () => format(subDays(new Date(), 90), 'yyyy-MM-dd')
const DEFAULT_END = () => format(addDays(new Date(), 30), 'yyyy-MM-dd')

function money(value: number, code: string): string {
  try {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    })
  } catch {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${code}`.trim()
  }
}

function RevenuePage() {
  const { token } = useAuth()
  // Committed range that drives the query — DateFilter commits on its own Apply,
  // so a partial edit never fires a request.
  const [applied, setApplied] = useState(() => ({ start: DEFAULT_START(), end: DEFAULT_END() }))

  const { data, isLoading, error } = useQuery({
    queryKey: ['ops-event-totals', applied],
    queryFn: () => apiClient.getOpsEventTotals(token ?? '', applied.start, applied.end),
    enabled: !!token,
  })

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Revenue</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
            Platform totals per event — selected by venue-local start date; figures are lifetime, net of refunds.
          </p>
        </div>

        <DateFilter
          value={{ gte: applied.start || undefined, lte: applied.end || undefined }}
          onChange={(v) => setApplied({ start: v.gte ?? '', end: v.lte ?? '' })}
          granularity="date"
          label="Event date"
        />
      </div>

      {error && (
        <p className="mb-4 text-sm text-amber-700 dark:text-amber-400">
          {(error as Error).message}
        </p>
      )}

      {/* Per-currency summary — EventPay Fee is the headline */}
      {data && data.totals_by_currency.length > 0 && (
        <div className="space-y-4 mb-6">
          {data.totals_by_currency.map((bucket) => (
            <TotalsCard key={bucket.currency} bucket={bucket} />
          ))}
        </div>
      )}

      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-x-auto">
        {isLoading ? (
          <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">Loading…</p>
        ) : !data || data.events.length === 0 ? (
          <p className="px-5 py-8 text-sm text-neutral-500 dark:text-neutral-400">
            No events start in this range.
          </p>
        ) : (
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-800">
                <th className="text-left font-medium px-5 py-3">Event</th>
                <th className="text-left font-medium px-3 py-3">Date</th>
                <th className="text-right font-medium px-3 py-3">Users</th>
                <th className="text-right font-medium px-3 py-3">Total Sold</th>
                <th className="text-right font-medium px-3 py-3">Add'l Fees</th>
                <th className="text-right font-medium px-3 py-3">Total Income</th>
                <th className="text-right font-medium px-3 py-3">Tax Collected</th>
                <th className="text-right font-medium px-3 py-3">Total Collected</th>
                <th className="text-right font-medium px-3 py-3">Platform Fee</th>
                <th className="text-right font-medium px-3 py-3">Mark Up</th>
                <th className="text-right font-medium px-5 py-3">EventPay Fee</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((row) => <EventTotalsRow key={row.id} row={row} />)}
            </tbody>
          </table>
        )}
      </div>

      <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500">
        Figures are net of refunds and cover paid commerce only — complimentary and voucher tokens
        are excluded (the per-event Financials page still counts those in its fee base). Direct Pay
        base, tax and fees come from the itemized purchase ledger; top-up refund fees reverse per
        parent purchase at that purchase&apos;s own rates. Mark Up is the margin on card processing
        (the organizer&apos;s processing fee minus our processor cost) from settled Finix data; EventPay
        Fee is the platform fee plus that mark up. A &ldquo;pending&rdquo; badge means card transactions
        are still awaiting settlement, so the mark up will grow.
      </p>
    </div>
  )
}

function TotalsCard({ bucket }: { bucket: OpsCurrencyTotals }) {
  const cells: Array<[string, number]> = [
    ['Total Sold', bucket.sold],
    ["Add'l Fees", bucket.additional_fees],
    ['Total Income', bucket.income],
    ['Tax Collected', bucket.tax],
    ['Total Collected', bucket.collected],
    ['Platform Fee', bucket.eventpay_fee],
    ['Mark Up', bucket.markup ?? 0],
  ]
  const headline = bucket.eventpay_fee_total ?? bucket.eventpay_fee + (bucket.markup ?? 0)
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-5 flex flex-col lg:flex-row lg:items-center gap-5">
      {/* Headline: the platform's cut, with what it's across */}
      <div className="shrink-0 lg:w-64">
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {bucket.currency} · {bucket.events} event{bucket.events === 1 ? '' : 's'} ·{' '}
          {bucket.users_sum.toLocaleString()} users*
        </p>
        <p className="mt-1 text-3xl font-semibold text-green-700 dark:text-green-400 tabular-nums">
          {money(headline, bucket.currency)}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
          EventPay Fee
          <span className="text-neutral-400 dark:text-neutral-500">platform fee + mark up</span>
          {(bucket.processing_pending ?? 0) > 0 && <PendingBadge count={bucket.processing_pending} />}
        </p>
        <p className="mt-2 text-[10px] text-neutral-400 dark:text-neutral-500">
          *sum of per-event uniques — one person at two events counts twice
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-x-6 gap-y-4 flex-1 lg:border-l lg:border-neutral-200 dark:lg:border-neutral-800 lg:pl-6">
        {cells.map(([label, value]) => (
          <div key={label}>
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</p>
            <p className="mt-1 text-xl font-semibold tabular-nums text-neutral-900 dark:text-white">
              {money(value, bucket.currency)}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

function EventTotalsRow({ row }: { row: OpsEventTotalsRow }) {
  let dateLabel = '—'
  try {
    // The event's own calendar date, in its own timezone.
    dateLabel = formatInTimeZone(new Date(row.start_date_time), row.timezone, 'MMM d, yyyy')
  } catch {
    dateLabel = row.start_date_time.slice(0, 10)
  }

  return (
    <tr className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
      <td className="px-5 py-3">
        <Link
          to="/events/$eventId/financials"
          params={{ eventId: String(row.id) }}
          className="flex items-center gap-3 group"
        >
          {row.logo ? (
            <img src={row.logo} alt="" className="w-8 rounded object-cover shrink-0" />
          ) : (
            <span className="h-8 w-8 rounded bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-semibold text-neutral-500 dark:text-neutral-300 shrink-0">
              {row.name.charAt(0)}
            </span>
          )}
          <span className="min-w-0">
            <span className="block font-medium text-neutral-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 truncate max-w-[280px]">
              {row.name}
            </span>
            {row.organization && (
              <span className="block text-xs text-neutral-500 dark:text-neutral-400 truncate max-w-[280px]">
                {row.organization}
              </span>
            )}
          </span>
        </Link>
      </td>
      <td className="px-3 py-3 text-neutral-600 dark:text-neutral-400">{dateLabel}</td>
      <td className="px-3 py-3 text-right tabular-nums">{row.users.toLocaleString()}</td>
      <td className="px-3 py-3 text-right tabular-nums">{money(row.sold, row.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums">{money(row.additional_fees, row.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums">{money(row.income, row.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums">{money(row.tax, row.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums font-medium">{money(row.collected, row.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums">{money(row.eventpay_fee, row.currency)}</td>
      <td className="px-3 py-3 text-right tabular-nums">
        <span className="inline-flex items-center gap-2 justify-end">
          {(row.processing_pending ?? 0) > 0 && <PendingBadge count={row.processing_pending} />}
          {money(row.markup ?? 0, row.currency)}
        </span>
      </td>
      <td className="px-5 py-3 text-right tabular-nums font-semibold text-green-700 dark:text-green-400">
        {money(row.eventpay_fee_total ?? row.eventpay_fee + (row.markup ?? 0), row.currency)}
      </td>
    </tr>
  )
}

function PendingBadge({ count }: { count?: number }) {
  const title = count
    ? `${count.toLocaleString()} card transaction${count === 1 ? '' : 's'} awaiting settlement`
    : 'Card transactions awaiting settlement'
  return (
    <span
      title={title}
      className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300"
    >
      Pending
    </span>
  )
}
